import { AppContext, AppContextType } from '@/AppContext'
import FakeMediaStreamTrack from '@/core/AudioEngine/FakeMediaStreamTrack'
import useDebounce2 from '@/hooks/useDebounce2'
import { AudioEngineRemoteStreamError, getAudioEngineRemoteDeviceError, encodeExtendedDeviceId } from '@/Logic'
import { SfuRemoteAudio, P2pRemoteAudio, AudioEngineRemoteAudio, RoomContext, RoomContextType } from '@/RoomContext'
import { FeatureFlags } from '@/services/FeatureFlags'
import { persistent } from '@/services/LocalStorage'
import { logger } from '@/services/Logger'
import { MusePeer } from '@/services/RoomContextMusePeer'
import useRoomActions from '@/services/useRoomActions'
import { UserContext, UserContextType } from '@/UserContext'
import { RemoteAudioTrack } from 'livekit-client'
import { useContext, useState, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utlis/cn'
import { Button } from '@/UIComponents2/Button'
import GearIcon from '@/CustomIcons/GearIcon'
import { Slider } from '@/UIComponents2/Slider'
import { SidebarTooltip as Tooltip } from '@/pages/SessionPage/internal/sidebar/SidebarTooltip'
import MicrophoneOffIcon from '@/CustomIcons/MicrophoneOffIcon'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import MenuButton, { MenuItem } from '@/UIComponents2/MenuButton/MenuButton'
import RecordAudioIcon from '@/CustomIcons/RecordAudioIcon'
import HeadphonesIcon from '@/CustomIcons/HeadphonesIcon'
import TimeSquareIcon from '@/CustomIcons/TimeSquareIcon'
import FolderCurvedIcon from '@/CustomIcons/FolderCurvedIcon'
import ElectronIPC from '@/services/ElectronIPC'
import useAppSettingsActions from '@/actions/useAppSettingsActions'
import { ErrorIndicator } from '@/pages/SessionPage/internal/shared/ErrorIndicator'
import LowCellSignalIcon from '@/CustomIcons/LowCellSignalIcon'
import { DeviceLabelRow } from './DeviceLabelRow'
import { DeviceContainer } from './DeviceContainer'
import { ChannelGroupContainer } from './ChannelGroupContainer'
import { ChannelGroupMeter } from './ChannelGroupMeter'
import { sessionSettingsTrigger } from '@/pages/SessionPage/SessionPage'
import { carmackClick } from '@/utils'

const initialVolume = 1

const log = logger.createChildLogger({ name: 'PeerControlPanel' })

export function AudioDevicePeer({
    peer,
    media,
    onShowDeviceSettingsDialog,
}: {
    peer: MusePeer
    media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio
    onShowDeviceSettingsDialog: (
        peer: MusePeer,
        media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio
    ) => void
}) {
    const { state } = useContext(UserContext) as UserContextType
    const { t } = useTranslation()

    const { changeRemoteAudioVolume } = useRoomActions()

    const channelSelection = (() => {
        if (media.info.channelSelection) {
            return media.info.channelSelection
        }

        const selection = []
        // @ts-ignore -- note, reportedChannelCount existed on older versions before the AudioEngine.
        const reportedChannelCount: number = media.info.reportedChannelCount || 2
        for (let i = 0; i < reportedChannelCount; ++i) {
            selection.push(i)
        }
        return [selection]
    })()

    const prevVolumeRef = useRef<number>(initialVolume)

    const onVolumeChangedHandler = (gainValue: number, chgrp: number[]) => {
        console.log(
            `*** onVolumeChangedHandler: streamUuid=${media.track.id} gainValue=${gainValue} chgrp=` +
                JSON.stringify(chgrp)
        )
        if (media.track instanceof FakeMediaStreamTrack) {
            media.track.setVolumeOfChannelGroup(gainValue, chgrp)
            persistent.set(`peerAudioVolumeCacheByDeviceId.${peer.appSerialId}-${media.info.label}`, media.track.volume)
        } else if (media.fromTopology === 'sfu') {
            // if sfu, we can just change the volume here using LiveKit.
            try {
                const d = media as unknown as RemoteAudioTrack
                d.setVolume(gainValue)
            } catch (e) {}
            persistent.set(`peerAudioVolumeCacheByDeviceId.${peer.appSerialId}-${media.info.label}`, gainValue)
        } else {
            // Send an rtc command telling the peer to change the volume from their end, on
            // this user's MusePeer instance.
            if (gainValue !== prevVolumeRef.current) {
                prevVolumeRef.current = gainValue
                changeRemoteAudioVolume(peer, media.info.deviceId, gainValue)
            }
            persistent.set(`peerAudioVolumeCacheByDeviceId.${peer.appSerialId}-${media.info.label}`, gainValue)
        }
    }

    const label = 'info' in media ? media.info.displayLabel || media.info.label || '' : ''

    const [audioEngineWarning, set_audioEngineWarning] = useState<AudioEngineRemoteStreamError | null>(null)

    useEffect(() => {
        let timer: NodeJS.Timer | undefined = undefined
        let warningMessagePrev = ''
        if (media.track instanceof FakeMediaStreamTrack) {
            timer = setInterval(() => {
                if (!(media.track instanceof FakeMediaStreamTrack)) {
                    return
                }

                const err = getAudioEngineRemoteDeviceError(state.mediaStreams.audioEngineAudioOutputError, media.track)

                const warningMessage = err?.message || ''
                if (warningMessage !== warningMessagePrev) {
                    if (warningMessage) {
                        log.error('ae_remote_device_quality_warning_triggered', '', {
                            deviceInfo: media?.info,
                            stats: media?.track?.remoteStats,
                        })
                    } else {
                        log.info('ae_remote_device_quality_warning_cleared', '', {
                            deviceInfo: media?.info,
                            stats: media?.track?.remoteStats,
                        })
                    }
                }

                set_audioEngineWarning(err)
                warningMessagePrev = warningMessage
            }, 1500)
        }

        return () => {
            clearInterval(timer)
        }
    }, [media.track, state.mediaStreams.audioEngineAudioOutputError])

    return (
        <DeviceContainer>
            <DeviceLabelRow
                label={label}
                info={media.info}
                hasError={audioEngineWarning?.severity === 'error'}
                onOpenSettings={() => onShowDeviceSettingsDialog(peer, media)}
            >
                {audioEngineWarning && (
                    <Tooltip text={audioEngineWarning.message}>
                        {audioEngineWarning.severity === 'error' ? (
                            <ErrorIndicator
                                text={t('Error occurred: click to open settings.')}
                                onClick={() => onShowDeviceSettingsDialog(peer, media)}
                            />
                        ) : (
                            <LowCellSignalIcon className="text-muse-yellow" />
                        )}
                    </Tooltip>
                )}
            </DeviceLabelRow>

            <div className="flex flex-col gap-4">
                {channelSelection.map((chgrp) => {
                    return (
                        <ChannelGroupVolumeSlider
                            key={JSON.stringify(chgrp)}
                            peer={peer}
                            media={media}
                            chgrp={chgrp}
                            onVolumeChangedHandler={onVolumeChangedHandler}
                        />
                    )
                })}
            </div>
        </DeviceContainer>
    )
}

function ChannelGroupVolumeSlider({
    peer,
    media,
    chgrp,
    onVolumeChangedHandler,
}: {
    peer: MusePeer
    media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio
    chgrp: number[]
    onVolumeChangedHandler: (gainValue: number, chgrp: number[]) => void
}) {
    const { t } = useTranslation()

    const { toggleRemoteAudioMute, toggleRemoteAudioSolo, calcRemoteAudioDeviceIsMutedByOwner } = useRoomActions()

    const _onVolumeChangedHandler = (value: number) => {
        onVolumeChangedHandler(value, chgrp)
    }

    const { setValue: setDebounceGainValue, value } = useDebounce2(100, 100, _onVolumeChangedHandler)

    const extDeviceId = encodeExtendedDeviceId(media.info.deviceId, chgrp)

    const isSolo = !!peer.remoteAudioContolStates[extDeviceId]?.isSoloed
    const isMutedByMe = !!peer.remoteAudioContolStates[extDeviceId]?.isMuted
    const isMutedBySolo = !!peer.remoteAudioContolStates[extDeviceId]?.isMutedBySolo
    const isMutedByOwner = calcRemoteAudioDeviceIsMutedByOwner(media, chgrp, peer.manifest)
    const isMuted = isMutedByMe || isMutedByOwner || isMutedBySolo

    let muteButtonTooltip = isMutedByMe ? t('Unmute this device') : t('Mute this device')
    if (isMutedByOwner) {
        muteButtonTooltip = t('Muted by the owner of this track')
    } else if (isMutedBySolo) {
        muteButtonTooltip = 'Muted because another device is soloed'
    }

    const recordButtonTooltipText = (() => {
        if (FeatureFlags.isEnabled('desktop-app-only-feature')) {
            return (
                <div className="max-w-[300px]">
                    {t('Click to record this device.') +
                        ' ' +
                        t('By default, recordings are saved to a file.') +
                        ' ' +
                        t('To record audio into your DAW, follow the Audio Recording tutorial.')}
                </div>
            )
        } else {
            return t('You need to download the Muse app for this feature.')
        }
    })()

    const volumeSliderOnChangeIsFirst = useRef(false)

    const { getCachedRemoteAudioVolume } = useRoomActions()

    const defaultValue = useMemo(() => getCachedRemoteAudioVolume(peer, media, chgrp), [])

    let meterTooltip: any = null

    const chLabel = chgrp.map((n) => n + 1).join('-')

    if (isMutedByOwner) {
        meterTooltip = t('Muted by the owner of this track')
    } else if (isMutedBySolo) {
        meterTooltip = 'Muted because another device is soloed'
    }

    return (
        <ChannelGroupContainer>
            <ChannelGroupMeter
                atrack={media.track}
                chgrp={chgrp}
                chLabel={chLabel}
                isMuted={isMuted}
                mutedStateTooltipText={meterTooltip}
            />

            <Tooltip text={meterTooltip}>
                <div className="grow">
                    <Slider
                        disabled={!!isMuted}
                        min={0}
                        max={2}
                        step={0.01}
                        value={value ?? defaultValue}
                        onChange={(newValue) => {
                            if (volumeSliderOnChangeIsFirst.current) {
                                volumeSliderOnChangeIsFirst.current = false
                            } else {
                                setDebounceGainValue(newValue)
                            }
                        }}
                    />
                </div>
            </Tooltip>

            <div className="flex gap-[2px]">
                <Tooltip variant="info" text={muteButtonTooltip}>
                    <Button
                        variant="tertiary"
                        minimal
                        className={cn(
                            'flex flex-col gap-[2px] items-center h-[30px] w-[30px] rounded-[10px]',
                            !!isMuted ? 'text-muse-danger' : 'text-muse-fg'
                        )}
                        onMouseDown={carmackClick(() =>
                            toggleRemoteAudioMute(peer, encodeExtendedDeviceId(media.info.deviceId, chgrp))
                        )}
                    >
                        {!!isMuted ? (
                            <MicrophoneOffIcon style={{ fontSize: '1.3rem' }} />
                        ) : (
                            <MicrophoneIcon style={{ fontSize: '1.3rem' }} />
                        )}
                    </Button>
                </Tooltip>

                <Tooltip
                    variant="info"
                    text={
                        isSolo ? (
                            t('Disable solo')
                        ) : (
                            <div className="max-w-[300px]">
                                {t('Solo this device to silence all devices that are not also soloed')}
                            </div>
                        )
                    }
                >
                    <Button
                        variant="tertiary"
                        minimal
                        className={cn(
                            'flex flex-col items-center h-[30px] w-[30px] rounded-[10px]',
                            isSolo
                                ? 'text-black bg-[radial-gradient(526.28%_147.67%_at_0.42%_9.62%,#FCE640_0%,#F49629_77.07%,#F17E22_100%)] hover:bg-[radial-gradient(ellipse_160%_150%_at_0.42%_9.62%,#FCE640_0%,#F49629_40%,#F17E22_100%)]'
                                : 'text-muse-fg'
                        )}
                        onMouseDown={carmackClick(() =>
                            toggleRemoteAudioSolo(peer, encodeExtendedDeviceId(media.info.deviceId, chgrp))
                        )}
                    >
                        <HeadphonesIcon style={{ fontSize: '1.3rem' }} />
                    </Button>
                </Tooltip>

                <RecordButton peer={peer} media={media} recordButtonTooltipText={recordButtonTooltipText} />
            </div>
        </ChannelGroupContainer>
    )
}

function RecordButton({
    peer,
    media,
    recordButtonTooltipText,
}: {
    peer: MusePeer
    media: SfuRemoteAudio | P2pRemoteAudio | AudioEngineRemoteAudio
    recordButtonTooltipText: React.ReactNode
}) {
    const { state: appState } = useContext(AppContext) as AppContextType
    const { state: roomState } = useContext(RoomContext) as RoomContextType
    const { t } = useTranslation()

    const { updateAppSettingItem } = useAppSettingsActions()

    const { toggleRemoteAudioRecord, calcCurrRemoteRecordingPeerAudio } = useRoomActions()

    const isRecording: boolean | 'stopping' = peer.remoteAudioRecordingState[media.info.deviceId]?.isRecording || false
    const isRecordingButtonDisabled = (() => {
        if (FeatureFlags.isEnabled('desktop-app-only-feature')) {
            //if (media.info.isAudioEngine) {
            //    return true
            //} else {
            if (isRecording === true) {
                return false
            } else {
                return !!calcCurrRemoteRecordingPeerAudio(roomState) || isRecording === 'stopping'
            }
            //}
        } else {
            return true
        }
    })()

    return (
        <MenuButton
            triggerClassName="h-[30px] max-h-[30px] rounded-[10px] shadow-glow md:grid-cols-[1fr_40%] md:min-w-[50px] bg-white/3"
            innerButtonClassName="bg-transparent px-0 md:px-0"
            disabled={isRecordingButtonDisabled}
            separator
            icon={
                isRecording ? (
                    <div className="bg-red-500 w-[12px] h-[12px] rounded-[4px]" />
                ) : (
                    <RecordAudioIcon style={{ fontSize: '1.2rem' }} />
                )
            }
            tooltip={{
                text: recordButtonTooltipText,
                variant: 'info',
            }}
            onIconClick={() => {
                !isRecordingButtonDisabled &&
                    toggleRemoteAudioRecord(peer, media.info.deviceId, media.info.displayLabel)
            }}
            placement="bottom"
            contentClassName="gap-[5px] p-0"
            content={
                <div className="flex flex-col gap-[5px] items-center relative">
                    <MenuItem asChild>
                        <Button
                            variant="rectangle"
                            className="self-stretch"
                            onClick={() => {
                                if (appState.appState.mostRecentRemoteRecordingInfo?.path) {
                                    ElectronIPC.openPath(appState.appState.mostRecentRemoteRecordingInfo.directory)
                                }
                            }}
                        >
                            {<TimeSquareIcon />}
                            {t('Recent Recording')}
                        </Button>
                    </MenuItem>
                    <MenuItem asChild>
                        <Button
                            variant="rectangle"
                            className="self-stretch"
                            onClick={async () => {
                                const r = await ElectronIPC.openFileDialog({ properties: ['openDirectory'] })
                                const selectedDirectoryPath = (r?.paths || [])[0] || null
                                if (selectedDirectoryPath) {
                                    updateAppSettingItem('remoteRecordSaveDirectory', selectedDirectoryPath, 'string')
                                }
                            }}
                        >
                            {<FolderCurvedIcon />}
                            {t('Save Location')}
                        </Button>
                    </MenuItem>
                    <MenuItem asChild>
                        <Button
                            variant="rectangle"
                            className="self-stretch text-muse-yellow"
                            onClick={() => {
                                sessionSettingsTrigger.trigger({ tab: 'recording' })
                            }}
                        >
                            {<GearIcon />}
                            {t('More Settings')}
                        </Button>
                    </MenuItem>
                </div>
            }
        />
    )
}
